import { cn } from "@/lib/cn";

/**
 * The ladder's angle against the 4:1 rule, as an instrument.
 *
 * ── Why the domain is ±10° and not 0-90° ───────────────────────────────────
 *
 * A gauge that spans every angle a ladder could physically be put at wastes
 * nine tenths of its arc on readings nobody ever takes, and squeezes the two
 * degrees that decide pass or fail into three pixels. Ten degrees either side of
 * the target is the range a real set-up lands in, and it makes the tolerance
 * band big enough to see the needle sitting inside or outside it.
 *
 * The band is drawn *and* the verdict is written out beside the figure. Colour
 * is never the only carrier: roughly 8% of this workforce has a red/green
 * deficiency, and "77.8°" with a green arc behind it is not a reading they can
 * take.
 */
export function AngleGauge({
  angle,
  target,
  tolerance,
  className,
}: {
  angle: number;
  target: number;
  tolerance: number;
  className?: string;
}) {
  const SPAN = 10;
  const min = target - SPAN;
  const max = target + SPAN;

  const CX = 100;
  const CY = 96;
  const R = 78;

  // Domain → the semicircle from 180° (left) to 0° (right).
  const angleAt = (value: number) => {
    const clamped = Math.min(max, Math.max(min, value));
    return 180 - ((clamped - min) / (max - min)) * 180;
  };

  const point = (degrees: number, radius: number) => {
    const rad = (degrees * Math.PI) / 180;
    return { x: CX + Math.cos(rad) * radius, y: CY - Math.sin(rad) * radius };
  };

  const arc = (from: number, to: number, radius: number) => {
    const a = point(from, radius);
    const b = point(to, radius);
    // Always the short way round: the whole sweep is a semicircle, so no arc
    // drawn on it can ever exceed 180°.
    return `M${a.x.toFixed(1)},${a.y.toFixed(1)} A${radius},${radius} 0 0 1 ${b.x.toFixed(1)},${b.y.toFixed(1)}`;
  };

  const needle = point(angleAt(angle), R - 6);
  const inTolerance = Math.abs(angle - target) <= tolerance;

  return (
    <svg
      viewBox="0 0 200 116"
      className={cn("gauge", className)}
      role="img"
      aria-label={`${angle.toFixed(1)} degrees against a ${target} degree target, tolerance plus or minus ${tolerance} degrees`}
    >
      <path d={arc(180, 0, R)} className="gauge__track" />
      <path
        d={arc(angleAt(target - tolerance), angleAt(target + tolerance), R)}
        className="gauge__band"
      />
      <line
        x1={point(angleAt(target), R - 14).x}
        y1={point(angleAt(target), R - 14).y}
        x2={point(angleAt(target), R + 6).x}
        y2={point(angleAt(target), R + 6).y}
        className="gauge__target"
      />

      <line
        x1={CX}
        y1={CY}
        x2={needle.x}
        y2={needle.y}
        className={cn("gauge__needle", inTolerance ? "gauge__needle--ok" : "gauge__needle--off")}
      />
      <circle cx={CX} cy={CY} r={5} className="gauge__hub" />

      <text x={CX - R} y={CY + 15} className="gauge__end">
        {min.toFixed(0)}°
      </text>
      <text x={CX + R} y={CY + 15} className="gauge__end gauge__end--right">
        {max.toFixed(0)}°
      </text>
    </svg>
  );
}
